/**
 * A tiny, always-on diagnostics ring buffer for the audio player.
 *
 * The iOS lock-screen bugs this exists for (see the header comment of
 * audioPlayer.tsx) are unreproducible on a dev machine — no attached
 * debugger survives the screen lock that triggers them. This buffer is the
 * only way to see what actually happened on the owner's phone: it lives in
 * localStorage (best effort — private mode / quota can silently no-op),
 * survives the app being backgrounded and reopened, and gets read out
 * through Settings → "Journal du lecteur" with a Copier button so the
 * owner can paste it after reproducing the bug.
 *
 * Kept deliberately dumb: no levels, no filtering, just a bounded list of
 * {when, what, detail} pushed in order. Bounded so it can't grow into a
 * storage problem on a device that's been playing stories for months.
 */

export interface PlayerLogEntry {
  atMs: number;
  event: string;
  detail?: Record<string, unknown>;
}

const MAX_ENTRIES = 200;
const STORAGE_KEY = "chouette.playerLog.v1";

function load(): PlayerLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_ENTRIES) as PlayerLogEntry[];
  } catch {
    return [];
  }
}

let buffer: PlayerLogEntry[] = load();

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    /* best effort — private browsing, quota, etc. */
  }
}

/** Record one diagnostic event. Never throws — a logging failure must never
 *  take playback down with it. */
export function logPlayerEvent(event: string, detail?: Record<string, unknown>): void {
  try {
    buffer.push({ atMs: Date.now(), event, detail });
    if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES);
    persist();
  } catch {
    /* best effort */
  }
}

/** A copy of the buffer, oldest first. */
export function getPlayerLog(): PlayerLogEntry[] {
  return [...buffer];
}

export function clearPlayerLog(): void {
  buffer = [];
  persist();
}

/** Plain-text rendering — what actually goes on the clipboard. One line per
 *  entry, ISO timestamp first so entries from across an app restart still
 *  sort and diff sanely. */
export function formatPlayerLog(): string {
  return buffer
    .map((e) => {
      const time = new Date(e.atMs).toISOString();
      const detail = e.detail ? ` ${JSON.stringify(e.detail)}` : "";
      return `${time} ${e.event}${detail}`;
    })
    .join("\n");
}
